/**
 * Antigüedad de Saldo CXP — migración web de ares8530.
 *
 * Aging de facturas de cuentas por pagar: buckets 0-30/31-60/61-90/+90
 * calculados por el backend a la fecha de corte (modo "A la fecha") o
 * sobre un rango de fechas de factura. Vista detallada (por factura) y
 * resumida (por suplidor). Filtro opcional por suplidor. Exporta a
 * Excel e imprime.
 *
 * Nota de migración: el reporte resumido del escritorio tenía SQL
 * inválido y nunca producía resultados correctos; aquí ambos modos
 * (detallado y resumido) salen del mismo cálculo del servidor.
 */
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { fechaDMY, useApi } from "lib/api";
import { imprimirListadoGrande } from "lib/impresionhtml";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin datos — procese el reporte
      </Typography>
    </Stack>
  );
}

function ResumenChip({ label, value, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
    </Box>
  );
}

// Delegado al formato compartido estilo factura x mayor (lib/impresionhtml).
function imprimirListado(titulo, subtitulo, cabeceras, filas, totales) {
  imprimirListadoGrande({ titulo, subtitulo, cabeceras, filas, totales });
}

const moneyCol = (field, headerName, width = 110) => ({
  field,
  headerName,
  width,
  align: "right",
  headerAlign: "right",
  valueFormatter: (value) => fmtMoney(value),
});

export default function AntiguedadSaldoCxp() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [tab, setTab] = useState(0);
  const [modo, setModo] = useState("alafecha");
  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [suplidores, setSuplidores] = useState([]);
  const [suplidor, setSuplidor] = useState(null);

  const [facturas, setFacturas] = useState([]);
  const [porsuplidor, setPorsuplidor] = useState([]);
  const [totales, setTotales] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    post("/api/cxp_suplidores", {}).then(({ ok, data }) => {
      if (ok) setSuplidores(data.suplidores || []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const procesar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/cxp_aging", {
        modo,
        fechadesde: fecha1,
        fechahasta: fecha2,
        suplidor: suplidor?.id || "",
      });
      if (ok) {
        setFacturas(data.facturas || []);
        setPorsuplidor(data.porsuplidor || []);
        setTotales(data.totales || null);
      } else {
        showAlert({ message: data.Error || "No se pudo generar el reporte", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const subtitulo = `${modo === "alafecha" ? `Al ${fechaDMY(fecha2)}` : `Del ${fechaDMY(fecha1)} al ${fechaDMY(fecha2)}`}${
    suplidor ? ` · ${suplidor.descrip}` : ""
  }`;

  const exportar = () => {
    const stamp = hoyISO();
    let hoja;
    if (tab === 0) {
      hoja = XLSX.utils.aoa_to_sheet([
        ["Antigüedad de Saldo CXP — Detallado"],
        [subtitulo],
        [],
        [
          "Fact#",
          "Fecha",
          "Suplidor",
          "Valor",
          "Aplicado",
          "Avance",
          "Balance",
          "Días",
          "0-30",
          "31-60",
          "61-90",
          "+90",
        ],
        ...facturas.map((f) => [
          f.nfactura,
          f.fecha,
          f.suplidornombre,
          f.valor,
          f.vplicado,
          f.vavance,
          f.balance,
          f.dias,
          f.t0a30,
          f.t31a60,
          f.t61a90,
          f.tmas90,
        ]),
        [],
        [
          "",
          "",
          "",
          "",
          "",
          "TOTAL",
          totales?.balance || 0,
          "",
          totales?.t0a30 || 0,
          totales?.t31a60 || 0,
          totales?.t61a90 || 0,
          totales?.tmas90 || 0,
        ],
      ]);
    } else {
      hoja = XLSX.utils.aoa_to_sheet([
        ["Antigüedad de Saldo CXP — Resumido"],
        [subtitulo],
        [],
        ["Código", "Suplidor", "0-30", "31-60", "61-90", "+90", "Balance"],
        ...porsuplidor.map((s) => [
          s.suplidor,
          s.suplidornombre,
          s.t0a30,
          s.t31a60,
          s.t61a90,
          s.tmas90,
          s.balance,
        ]),
        [],
        [
          "",
          "TOTAL",
          totales?.t0a30 || 0,
          totales?.t31a60 || 0,
          totales?.t61a90 || 0,
          totales?.tmas90 || 0,
          totales?.balance || 0,
        ],
      ]);
    }
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, tab === 0 ? "Detallado" : "Resumido");
    XLSX.writeFile(libro, `AntiguedadSaldoCXP_${stamp}.xlsx`);
  };

  const imprimir = () => {
    if (tab === 0) {
      imprimirListado(
        "Antigüedad de Saldo CXP — Detallado",
        subtitulo,
        ["Fact#", "Fecha", "Suplidor", "Balance", "Días", "0-30", "31-60", "61-90", "+90"],
        facturas.map((f) => [
          f.nfactura,
          f.fecha,
          f.suplidornombre,
          fmtMoney(f.balance),
          f.dias,
          fmtMoney(f.t0a30),
          fmtMoney(f.t31a60),
          fmtMoney(f.t61a90),
          fmtMoney(f.tmas90),
        ]),
        [
          "",
          "",
          "TOTAL",
          fmtMoney(totales?.balance),
          "",
          fmtMoney(totales?.t0a30),
          fmtMoney(totales?.t31a60),
          fmtMoney(totales?.t61a90),
          fmtMoney(totales?.tmas90),
        ]
      );
    } else {
      imprimirListado(
        "Antigüedad de Saldo CXP — Resumido",
        subtitulo,
        ["Código", "Suplidor", "0-30", "31-60", "61-90", "+90", "Balance"],
        porsuplidor.map((s) => [
          s.suplidor,
          s.suplidornombre,
          fmtMoney(s.t0a30),
          fmtMoney(s.t31a60),
          fmtMoney(s.t61a90),
          fmtMoney(s.tmas90),
          fmtMoney(s.balance),
        ]),
        [
          "",
          "TOTAL",
          fmtMoney(totales?.t0a30),
          fmtMoney(totales?.t31a60),
          fmtMoney(totales?.t61a90),
          fmtMoney(totales?.tmas90),
          fmtMoney(totales?.balance),
        ]
      );
    }
  };

  const columnasFacturas = [
    { field: "nfactura", headerName: "Fact#", width: 100 },
    { field: "fecha", headerName: "Fecha", width: 105 },
    { field: "suplidornombre", headerName: "Suplidor", flex: 1, minWidth: 170 },
    moneyCol("valor", "Valor"),
    moneyCol("vplicado", "Aplicado"),
    moneyCol("vavance", "Avance"),
    moneyCol("balance", "Balance"),
    { field: "dias", headerName: "Días", width: 70, align: "right", headerAlign: "right" },
    moneyCol("t0a30", "0-30", 100),
    moneyCol("t31a60", "31-60", 100),
    moneyCol("t61a90", "61-90", 100),
    moneyCol("tmas90", "+90", 100),
  ];

  const columnasPorsuplidor = [
    { field: "suplidor", headerName: "Código", width: 110 },
    { field: "suplidornombre", headerName: "Suplidor", flex: 1, minWidth: 200 },
    moneyCol("t0a30", "0-30", 115),
    moneyCol("t31a60", "31-60", 115),
    moneyCol("t61a90", "61-90", 115),
    moneyCol("tmas90", "+90", 115),
    moneyCol("balance", "Balance", 125),
  ];

  const hayDatos = tab === 0 ? facturas.length > 0 : porsuplidor.length > 0;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Antigüedad de Saldo CXP"
        subtitle="Balances pendientes de cuentas por pagar con antigüedad 0-30/31-60/61-90/+90"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Modo"
                value={modo}
                onChange={(e) => setModo(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="alafecha">A la fecha</MenuItem>
                <MenuItem value="rango">Rango de fechas</MenuItem>
              </TextField>
            </Grid>
            {modo === "rango" && (
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Desde"
                  InputLabelProps={{ shrink: true }}
                  value={fecha1}
                  onChange={(e) => setFecha1(e.target.value)}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Autocomplete
                options={suplidores}
                value={suplidor}
                onChange={(_, v) => setSuplidor(v)}
                getOptionLabel={(o) => o?.descrip || ""}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField {...params} label="Suplidor (opcional)" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={2} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={procesar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mb: 1.6 }} useFlexGap>
            <ResumenChip label="BALANCE" value={`RD$ ${fmtMoney(totales?.balance)}`} />
            <ResumenChip
              label="0-30 DÍAS"
              value={`RD$ ${fmtMoney(totales?.t0a30)}`}
              color="#2E7D32"
            />
            <ResumenChip
              label="31-60 DÍAS"
              value={`RD$ ${fmtMoney(totales?.t31a60)}`}
              color="#F9A825"
            />
            <ResumenChip
              label="61-90 DÍAS"
              value={`RD$ ${fmtMoney(totales?.t61a90)}`}
              color="#EF6C00"
            />
            <ResumenChip
              label="+90 DÍAS"
              value={`RD$ ${fmtMoney(totales?.tmas90)}`}
              color="#C62828"
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportar}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.success)}
            >
              Excel
            </Button>
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimir}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
          </Stack>

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 700 } }}
          >
            <Tab label="Detallado" />
            <Tab label="Resumido" />
          </Tabs>

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={tab === 0 ? facturas : porsuplidor}
              columns={tab === 0 ? columnasFacturas : columnasPorsuplidor}
              getRowId={tab === 0 ? (r) => r.id : (r) => r.suplidor}
              loading={loading}
              rowHeight={44}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
